<template>
  <v-dialog
      :value="value"
      @input="$emit('input', $event)"
      max-width="900"
      scrollable
  >
    <v-card v-if="loading || !transaction">
      <v-card-title class="headline grey lighten-2">
        Carregando...
        <v-spacer></v-spacer>
        <v-btn icon @click="close">
          <i class="fas fa-times"></i>
        </v-btn>
      </v-card-title>
      <v-card-text class="pa-4 text-center">
        <v-progress-circular
            indeterminate
            color="primary"
            size="64"
        ></v-progress-circular>
        <p class="mt-4">Carregando detalhes da transação...</p>
      </v-card-text>
    </v-card>

    <v-card v-else-if="error">
      <v-card-title class="headline grey lighten-2">
        Erro ao Carregar
        <v-spacer></v-spacer>
        <v-btn icon @click="close">
          <i class="fas fa-times"></i>
        </v-btn>
      </v-card-title>
      <v-card-text class="pa-4 text-center">
        <i
            class="fas fa-exclamation-triangle"
            style="font-size: 48px; color: #ff5252"
        ></i>
        <p class="mt-4">{{ error }}</p>
        <v-btn color="primary" @click="loadTransaction" class="mt-2">
          Tentar Novamente
        </v-btn>
      </v-card-text>
    </v-card>

    <v-card v-else>
      <v-card-title class="headline grey lighten-2">
        <i class="fas fa-receipt mr-2"></i>
        {{ headerTitle }}
        <v-spacer></v-spacer>
        <v-btn icon @click="close">
          <i class="fas fa-times"></i>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-4">
        <transaction-details
            :transaction="transaction"
            :specific-details="transactionSpecificDetails"
        />
      </v-card-text>

      <v-card-actions class="px-4 pb-4 d-block">
        <v-btn
            color="success"
            block
            large
            @click="sendPdfEmail"
            :loading="emailLoading"
        >
          <i class="fas fa-envelope mr-2"></i>
          Enviar Comprovante por e-mail
        </v-btn>
      </v-card-actions>

      <v-card-actions class="px-4 pb-4 d-block">
        <v-btn
            color="primary"
            block
            large
            @click="requestPdfDownload"
            :loading="pdfLoading"
        >
          <i class="fas fa-file-pdf mr-2"></i>
          Baixar Comprovante em PDF
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from "axios";
import TransactionDetails from "./TransactionDetails";
import PdfExportService from "@/services/PdfExportService";
import {TRANSACTION_TYPES} from "@/constants/transaction";

export default {
  name: "TransactionDetailsDialog",
  components: {
    TransactionDetails,
  },
  props: {
    value: {
      type: Boolean,
      required: true,
    },
    transactionId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      transaction: null,
      loading: false,
      error: null,
      pdfLoading: false,
      emailLoading: false,
    };
  },
  computed: {
    headerTitle() {
      const hasTransaction = this.transaction;

      if (!hasTransaction) {
        return "Detalhes da Transação";
      }

      const typeId = this.transaction.type;

      switch (typeId) {
        case TRANSACTION_TYPES.PARKING_ACTIVATION_WALLET:
        case TRANSACTION_TYPES.PARKING_ACTIVATION_AUTOMATIC:
          return "Detalhes do Estacionamento";
        case TRANSACTION_TYPES.CREDIT_SALE:
          return "Detalhes da Compra de Crédito";
        case TRANSACTION_TYPES.NOTIFICATION_PAYMENT_BY_WALLET:
        case TRANSACTION_TYPES.NOTIFICATION_PAYMENT_BY_PAYMENT:
          return "Detalhes da Notificação";
        case TRANSACTION_TYPES.CREDIT_TRANSFER:
          return "Transferência de Crédito";
        default:
          return "Detalhes da Transação";
      }
    },
    transactionSpecificDetails() {
      const hasTransaction = this.transaction;

      if (!hasTransaction) {
        return null;
      }

      return this.getSpecificTransactionTypePrintData(this.transaction);
    },
  },
  watch: {
    value(isOpen) {
      const shouldLoadTransaction = isOpen && this.transactionId;

      if (shouldLoadTransaction) {
        this.loadTransaction();
      }
    },
    transactionId(newId) {
      const shouldLoadTransaction = newId && this.value;

      if (shouldLoadTransaction) {
        this.loadTransaction();
      }
    },
  },
  methods: {
    close() {
      this.$emit("input", false);
      this.transaction = null;
      this.error = null;
    },
    async loadTransaction() {
      const hasValidId = this.transactionId;

      if (!hasValidId) {
        this.error = "ID da transação não fornecido";
        return;
      }

      this.loading = true;
      this.error = null;

      try {
        const response = await axios.get(
            `/api/v2/transaction/${this.transactionId}`
        );
        this.transaction = response.data;
      } catch (e) {
        console.error("Erro ao carregar transação:", e);
        this.error =
            "Não foi possível carregar os detalhes da transação. Tente novamente.";
      } finally {
        this.loading = false;
      }
    },
    searchAssociationsFor(associations, name) {
      if (!associations) {
        return [];
      }

      const association = associations.find((assoc) => assoc.name === name);
      if (association) {
        return association.items;
      }

      return [];
    },
    getSpecificTransactionTypePrintData(transaction) {
      const transactionType = transaction.type;

      switch (transactionType) {
        case 17:
          return this.getNotificationPaymentDetails(transaction);
        case 6:
          return this.getParkingActivationDetails(transaction);
        case 30:
        case 31:
          return this.getSpecialTransactionDetails(transaction);
        default:
          return {};
      }
    },
    getNotificationPaymentDetails(transaction) {
      const notifications = this.searchAssociationsFor(
          transaction.associations,
          "ParkingNotification"
      );

      const hasBonusInfo =
          transaction.extra &&
          transaction.extra.had_bonus &&
          transaction.target;
      const bonusUser = hasBonusInfo ? transaction.target.name : null;

      return {
        transactions: notifications.map((notification) => ({
          id: notification.id,
          date_time: notification.date_time,
          plate: notification.plate,
        })),
        wallet_transfer: bonusUser,
      };
    },
    getParkingActivationDetails(transaction) {
      const activations = this.searchAssociationsFor(
          transaction.associations,
          "ParkingActivate"
      );

      const hasActivations = activations.length > 0;

      if (!hasActivations) {
        return {};
      }

      const activation = activations[0];
      const hasDetail = activation.detail;

      return {
        plate: activation.plate,
        product: hasDetail ? activation.detail.product.name : null,
        time: hasDetail ? activation.detail.time : null,
        date_time_begin: hasDetail ? activation.detail.date_time_begin : null,
        date_time_end: hasDetail ? activation.detail.date_time_end : null,
      };
    },
    getSpecialTransactionDetails(transaction) {
      const hasCreator = transaction.creator;
      const hasTarget = transaction.target;

      return {
        secondCopy: true,
        date: transaction.created_at,
        title: transaction.type_name,
        transactionId: transaction.id,
        creatorName: hasCreator ? transaction.creator.name : null,
        agentName: hasTarget ? transaction.target.name : null,
        payments: transaction.payments,
      };
    },
    requestPdfDownload() {
      if (this.pdfLoading) {
        return;
      }

      this.pdfLoading = true;

      const jobStartUrl = `/api/v2/transaction/print/${this.transactionId}`;

      this.$emit("request-pdf", jobStartUrl);

      this.pdfLoading = false;

      this.close();
    },
    async sendPdfEmail() {
      this.emailLoading = true;

      try {
        await PdfExportService.sendPdfEmailV2(this.transactionId);
        this.$emit(
            "show-snackbar",
            "O relatório em PDF foi enviado para o email cadastrado!",
            "success"
        );
      } catch (error) {
        console.error("Erro ao enviar email:", error);
        const errorMessage =
            error.message ||
            "Ocorreu um erro ao enviar o email. Tente novamente mais tarde.";
        this.$emit("show-snackbar", errorMessage, "error");
      } finally {
        this.emailLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.headline {
  font-weight: bold;
  position: sticky;
  top: 0;
  z-index: 1;
}
</style>
